import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { SITE_URL, principles, siteConfig, siteStats, testimonials, values } from "@/mocks/site";
import { buildBreadcrumbSchema } from "@/lib/seo";

export default function About() {
  const jsonLd = [
    buildBreadcrumbSchema([
      { name: "首页", path: "/" },
      { name: "关于我们", path: "/about" },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于黑料看点",
      url: `${SITE_URL}/about`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteConfig.name,
      url: `${SITE_URL}/`,
      email: siteConfig.contactEmail,
      foundingDate: String(siteConfig.foundedYear),
      description: siteConfig.description,
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 - 黑料看点的定位、理念与内容原则"
        description="了解黑料看点：我们为什么做每日精选、如何筛选看点、坚持怎样的内容原则，以及网站定位与读者口碑。"
        keywords="关于黑料看点,黑料看点定位,内容原则,每日精选"
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="bg-background-50 border-b border-background-200">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 pt-6 pb-10 md:pt-8 md:pb-14">
          <Breadcrumbs items={[{ name: "关于我们" }]} />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="text-xs tracking-[0.18em] text-primary-600 mb-3">关于 {siteConfig.name}</p>
              <h1 className="font-heading text-3xl md:text-4xl leading-snug text-foreground-950">
                我们只是想让你少翻一点，多看一点
              </h1>
              <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">
                {siteConfig.name}成立于 {siteConfig.foundedYear} 年，是一个围绕文娱与网络热点做每日精选的内容平台。
                我们不生产八卦，也不追着热度跑，只做一件事：把当天真正值得看的内容挑出来，整理成一页清单。
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                当信息足够多的时候，稀缺的从来不是内容，而是筛选。这就是我们存在的理由。
              </p>
            </div>
            <div className="lg:col-span-6">
              <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20desk%20scene%20with%20stacked%20newspapers%20a%20notebook%20and%20a%20cup%20of%20coffee%20in%20soft%20morning%20light%2C%20cream%20and%20amber%20tones%2C%20calm%20magazine%20photography%2C%20high%20detail&width=1200&height=900&seq=hlk-about-01&orientation=landscape"
                  alt="黑料看点编辑部日常工作场景示意"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 border-b border-background-200">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-2xl mb-10">
            <p className="text-xs tracking-[0.18em] text-primary-600 mb-2.5">网站定位与发展理念</p>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">我们相信的四件事</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              这些理念决定了我们每天怎么选题、怎么取舍，也决定了什么内容不会出现在这个网站上。
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {values.map((item, index) => (
              <div key={item.title} className="flex gap-4 p-6 rounded-lg bg-background-50 border border-background-200">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="text-base font-semibold text-foreground-950">
                    {String(index + 1).padStart(2, "0")} · {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 border-b border-background-200">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.18em] text-secondary-700 mb-2.5">内容原则</p>
              <h2 className="font-heading text-2xl md:text-3xl leading-snug text-foreground-950">
                边界感，是我们做内容的前提
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                热点阅读最容易滑向情绪消费。我们给自己划了几条清楚的线，并且在每天的选题里执行。
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {siteStats.map((item) => (
                  <div key={item.label} className="p-4 rounded-lg bg-background-100 border border-background-200">
                    <p className="font-heading text-xl leading-none text-foreground-950">{item.value}</p>
                    <p className="mt-1.5 text-xs text-foreground-500">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <ol className="flex flex-col gap-4">
                {principles.map((item, index) => (
                  <li key={item} className="flex gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="font-heading text-lg text-primary-600 shrink-0">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="text-sm leading-[1.85] text-foreground-700">{item}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 border-b border-background-200">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-2xl mb-10">
            <p className="text-xs tracking-[0.18em] text-primary-600 mb-2.5">读者口碑</p>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">来自读者的真实反馈</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex flex-col p-6 rounded-lg bg-background-50 border border-background-200">
                <blockquote className="text-sm leading-relaxed text-foreground-700 flex-1">{item.quote}</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-background-200 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full overflow-hidden bg-background-200 shrink-0">
                    <img src={item.avatar} alt={`${item.name}的头像`} className="w-full h-full object-cover object-top" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-10 rounded-lg bg-primary-950 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex-1">
              <h2 className="font-heading text-xl md:text-2xl text-background-50">想推荐一条看点，或者聊聊合作？</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-200/85">
                邮箱 {siteConfig.contactEmail} · 投稿线索也可以直接留言，我们会认真看完每一条。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-1.5 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-medium whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
            >
              联系我们 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}